import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Button
} from '@mui/material';
import {
  ChevronLeft,
  ChevronRight
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

const CustomCalendar = ({ 
  value, 
  onChange, 
  onClose,
  placeholder = "Select date"
}) => {
  const { t } = useTranslation();
  // Parse date string properly to avoid timezone issues
  const [currentDate, setCurrentDate] = useState(() => {
    if (value) {
      const [year, month, day] = value.split('-').map(Number);
      return new Date(year, month - 1, day, 12, 0, 0);
    }
    return new Date();
  });
  const [selectedDate, setSelectedDate] = useState(() => {
    if (value) {
      const [year, month, day] = value.split('-').map(Number);
      return new Date(year, month - 1, day, 12, 0, 0);
    }
    return null;
  });

  // Month navigation
  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(currentDate.getMonth() + direction);
    setCurrentDate(newDate);
  };

  // Get days in month
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1, 12, 0, 0); // Set to noon to avoid timezone issues
    const lastDay = new Date(year, month + 1, 0, 12, 0, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1; // Monday = 0
    
    const days = [];
    
    // Previous month's trailing days
    const prevMonth = new Date(year, month, 0, 12, 0, 0);
    const prevMonthDays = prevMonth.getDate();
    for (let i = startingDayOfWeek - 1; i >= 0; i--) {
      days.push({
        day: prevMonthDays - i,
        isCurrentMonth: false,
        date: new Date(year, month - 1, prevMonthDays - i, 12, 0, 0)
      });
    }
    
    // Current month's days
    for (let day = 1; day <= daysInMonth; day++) {
      days.push({
        day,
        isCurrentMonth: true,
        date: new Date(year, month, day, 12, 0, 0)
      });
    }
    
    // Next month's leading days
    const totalCells = 42; // 6 rows × 7 days
    const remainingCells = totalCells - days.length;
    for (let day = 1; day <= remainingCells; day++) {
      days.push({
        day,
        isCurrentMonth: false,
        date: new Date(year, month + 1, day, 12, 0, 0)
      });
    }
    
    return days;
  };

  const days = getDaysInMonth(currentDate);
  
  const weekDays = [
    t('calendar.mon', 'Mon'),
    t('calendar.tue', 'Tue'), 
    t('calendar.wed', 'Wed'),
    t('calendar.thu', 'Thu'),
    t('calendar.fri', 'Fri'),
    t('calendar.sat', 'Sat'),
    t('calendar.sun', 'Sun')
  ];

  const months = [
    t('calendar.january', 'January'),
    t('calendar.february', 'February'),
    t('calendar.march', 'March'),
    t('calendar.april', 'April'),
    t('calendar.may', 'May'),
    t('calendar.june', 'June'),
    t('calendar.july', 'July'),
    t('calendar.august', 'August'),
    t('calendar.september', 'September'),
    t('calendar.october', 'October'),
    t('calendar.november', 'November'),
    t('calendar.december', 'December')
  ];

  const handleDateClick = (day) => {
    if (!day.isCurrentMonth) {
      // Navigate to the clicked month and select the date
      setCurrentDate(new Date(day.date));
    }
    setSelectedDate(day.date);
    // Format date correctly to avoid timezone issues
    const year = day.date.getFullYear();
    const month = String(day.date.getMonth() + 1).padStart(2, '0');
    const dayOfMonth = String(day.date.getDate()).padStart(2, '0');
    const formattedDate = `${year}-${month}-${dayOfMonth}`;
    onChange(formattedDate);
    onClose();
  };

  const isSelected = (day) => {
    if (!selectedDate) return false;
    return day.date.toDateString() === selectedDate.toDateString();
  };

  const isToday = (day) => {
    const today = new Date();
    return day.date.toDateString() === today.toDateString();
  };

  return (
    <Paper 
      elevation={0}
      sx={{ 
        p: 2, 
        borderRadius: 3,
        boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
        border: '1px solid #e5e7eb',
        minWidth: 320
      }}
    >
      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        mb: 2 
      }}>
        <IconButton 
          onClick={() => navigateMonth(-1)}
          size="small"
          sx={{ 
            color: '#00C853',
            '&:hover': { 
              backgroundColor: '#f0fdf4' 
            }
          }}
        >
          <ChevronLeft />
        </IconButton>
        
        <Typography 
          variant="subtitle1" 
          sx={{ 
            fontWeight: 600,
            color: '#111827',
            fontSize: 16
          }}
        >
          {months[currentDate.getMonth()]} {currentDate.getFullYear()}
        </Typography>
        
        <IconButton 
          onClick={() => navigateMonth(1)}
          size="small"
          sx={{ 
            color: '#00C853',
            '&:hover': { 
              backgroundColor: '#f0fdf4' 
            }
          }}
        >
          <ChevronRight />
        </IconButton>
      </Box>

      {/* Week days header */}
      <Box sx={{ 
        display: 'grid',
        gridTemplateColumns: 'repeat(7, 1fr)',
        gap: 0.5,
        mb: 1
      }}>
        {weekDays.map((day, index) => (
          <Box key={index} sx={{ 
            textAlign: 'center',
            minWidth: 36,
            height: 24,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Typography 
              variant="caption" 
              sx={{ 
                color: '#6b7280',
                fontWeight: 500,
                fontSize: 12
              }}
            >
              {day}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Calendar days */}
      <Box sx={{ 
        display: 'grid',
        gridTemplateColumns: 'repeat(7, 1fr)',
        gap: 0.5
      }}>
        {days.slice(0, 42).map((day, index) => (
          <Button
            key={index}
            onClick={() => handleDateClick(day)}
            sx={{
              minWidth: 36,
              height: 36,
              padding: 0,
              borderRadius: 2,
              color: day.isCurrentMonth ? '#111827' : '#d1d5db',
              backgroundColor: isSelected(day) 
                ? '#00C853' 
                : isToday(day) 
                  ? '#f0fdf4' 
                  : 'transparent',
              fontSize: 14,
              fontWeight: isSelected(day) ? 600 : 400,
              '&:hover': {
                backgroundColor: isSelected(day) 
                  ? '#00A843' 
                  : '#f0fdf4',
                color: isSelected(day) ? 'white' : '#00C853'
              },
              ...(isSelected(day) && {
                color: 'white !important',
                '&:hover': {
                  color: 'white !important'
                }
              })
            }}
          >
            {day.day}
          </Button>
        ))}
      </Box>

      {/* Footer */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        gap: 1, 
        mt: 2,
        pt: 2,
        borderTop: '1px solid #f3f4f6'
      }}>
        <Button 
          variant="outlined" 
          onClick={() => {
            setSelectedDate(null);
            onChange('');
            onClose();
          }}
          sx={{
            borderColor: '#e5e7eb',
            color: '#6b7280',
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 500,
            '&:hover': {
              bgcolor: '#f3f4f6',
              borderColor: '#d1d5db',
            }
          }}
        >
          {t('datePicker.clear', 'Clear')}
        </Button>
        
        <Button 
          variant="outlined" 
          onClick={onClose}
          sx={{
            borderColor: '#e5e7eb',
            color: '#374151',
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 500,
            '&:hover': {
              bgcolor: '#f9fafb',
              borderColor: '#d1d5db',
            }
          }}
        >
          {t('common.cancel', 'Cancel')}
        </Button>
      </Box>
    </Paper>
  );
};

export default CustomCalendar;